import { Controller } from "@hotwired/stimulus"

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]
const PAID_CLICK_KEYS = ["gclid", "dclid", "msclkid", "yclid", "gbraid", "wbraid"]

export default class extends Controller {
  static values = { metaPixelId: String }

  connect() {
    this.lastTrackedPath = null
    this.lastMetaPageViewLocation = null
    this.captureAttribution()
    this.boundTrack = this.trackPageView.bind(this)
    this.boundSubmitEnd = this.handleSubmitEnd.bind(this)
    this.boundConsentClick = this.handleConsentClick.bind(this)
    document.addEventListener("turbo:load", this.boundTrack)
    document.addEventListener("turbo:submit-end", this.boundSubmitEnd)
    document.addEventListener("click", this.boundConsentClick)
  }

  disconnect() {
    this.lastTrackedPath = null
    document.removeEventListener("turbo:load", this.boundTrack)
    document.removeEventListener("turbo:submit-end", this.boundSubmitEnd)
    document.removeEventListener("click", this.boundConsentClick)
  }

  captureAttribution() {
    if (window._pillhubAttributionCaptured) return

    window._pillhubAttributionCaptured = true
    const params = new URLSearchParams(window.location.search)

    UTM_KEYS.forEach((key) => {
      sessionStorage.removeItem(`_vt_${key}`)

      const value = params.get(key)
      if (value) sessionStorage.setItem(`_vt_${key}`, value)
    })

    sessionStorage.setItem("_vt_referrer", document.referrer || "")
  }

  trackPageView() {
    this.trackVisit()
    if (this.trackMetaPageView()) this.trackMetaPageEvent()
    this.trackGooglePurchase()
    this.trackMetaPurchase()
  }

  trackVisit() {
    const currentLocation = this.trackedLocation()
    if (currentLocation === this.lastTrackedPath) return
    this.lastTrackedPath = currentLocation

    const data = {
      page_url: currentLocation,
      page_title: document.title
    }

    UTM_KEYS.forEach((key) => {
      const val = sessionStorage.getItem(`_vt_${key}`)
      if (val) data[key] = val
    })

    const ref = sessionStorage.getItem("_vt_referrer")
    if (ref) data["referrer"] = ref

    const token = document.querySelector('meta[name="csrf-token"]')

    fetch("/visits", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token ? token.content : ""
      },
      body: JSON.stringify(data),
      keepalive: true
    }).catch(() => {})
  }

  trackedLocation() {
    const params = new URLSearchParams(window.location.search)
    const attributionParams = new URLSearchParams()

    for (const key of [...UTM_KEYS, ...PAID_CLICK_KEYS]) {
      const value = params.get(key)
      if (value) attributionParams.set(key, value)
    }

    const query = attributionParams.toString()
    return query ? `${window.location.pathname}?${query}` : window.location.pathname
  }

  handleSubmitEnd(event) {
    if (!event.detail.success) return

    const payload = this.parsePayload(event.target.dataset.analyticsAddToCart)
    if (payload) this.trackMeta("AddToCart", payload)
  }

  handleConsentClick() {
    queueMicrotask(this.boundTrack)
  }

  openCookieSettings(event) {
    if (typeof window.cbotChangeConsent !== "function") return

    event.preventDefault()
    window.cbotChangeConsent()
  }

  trackMetaPageView() {
    const location = `${window.location.pathname}${window.location.search}`
    if (location === this.lastMetaPageViewLocation) return
    if (!this.trackMeta("PageView")) return

    this.lastMetaPageViewLocation = location
    return true
  }

  trackMetaPageEvent() {
    const element = document.querySelector("[data-analytics-page-event]")
    if (!element) return

    const event = this.parsePayload(element.dataset.analyticsPageEvent)
    if (!event) return

    this.trackMeta(event.name, event.data)
  }

  trackGooglePurchase() {
    const purchase = this.purchasePayload()
    const transactionId = purchase?.google?.transaction_id
    if (!transactionId || this.wasTracked("google", transactionId)) return

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({ event: "purchase", ecommerce: purchase.google })
    this.markTracked("google", transactionId)
  }

  trackMetaPurchase() {
    const purchase = this.purchasePayload()
    const transactionId = purchase?.google?.transaction_id
    if (!transactionId || this.wasTracked("meta", transactionId)) return
    if (!this.trackMeta("Purchase", purchase.meta)) return

    this.markTracked("meta", transactionId)
  }

  purchasePayload() {
    const element = document.querySelector("[data-analytics-purchase]")
    return this.parsePayload(element?.dataset.analyticsPurchase)
  }

  trackMeta(eventName, data) {
    if (window.cbotConsent?.()?.marketing !== true) return false
    if (!this.initializeMetaPixel()) return false

    if (data) {
      window.fbq("track", eventName, data)
    } else {
      window.fbq("track", eventName)
    }

    return true
  }

  initializeMetaPixel() {
    const pixelId = this.metaPixelIdValue
    if (!pixelId) return false

    if (!window.fbq) {
      !function(f,b,e,v,n,t,s) {
        if (f.fbq) return
        n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)}
        if (!f._fbq) f._fbq=n
        n.push=n
        n.loaded=true
        n.version="2.0"
        n.queue=[]
        t=b.createElement(e)
        t.async=true
        t.src=v
        s=b.getElementsByTagName(e)[0]
        s.parentNode.insertBefore(t,s)
      }(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js")
    }

    if (window._pillhubMetaPixelId !== pixelId) {
      window.fbq("init", pixelId)
      window._pillhubMetaPixelId = pixelId
    }

    return true
  }

  parsePayload(value) {
    return value ? JSON.parse(value) : null
  }

  wasTracked(provider, transactionId) {
    const key = this.trackingKey(provider, transactionId)

    try {
      return window.localStorage.getItem(key) === "1"
    } catch {
      window._pillhubTrackedEvents = window._pillhubTrackedEvents || new Set()
      return window._pillhubTrackedEvents.has(key)
    }
  }

  markTracked(provider, transactionId) {
    const key = this.trackingKey(provider, transactionId)

    try {
      window.localStorage.setItem(key, "1")
    } catch {
      window._pillhubTrackedEvents = window._pillhubTrackedEvents || new Set()
      window._pillhubTrackedEvents.add(key)
    }
  }

  trackingKey(provider, transactionId) {
    return `pillhub:${provider}:purchase:${transactionId}`
  }
}
